import { Controller } from "@hotwired/stimulus";
import IMask from "imask";
import { currencySymbols } from "../../../utils/createNumberPipe";
import type { ImaskedElement } from "../../inputs/imask_controller";

const getValidUnitOfMeasure = (
  currentUnit,
  defaultProductUnit,
  productEquivalencies
) => {
  if (currentUnit !== "" && currentUnit === defaultProductUnit) {
    return currentUnit;
  }

  if (
    productEquivalencies.find(
      ({ unitOfMeasure }) => currentUnit === unitOfMeasure
    )
  ) {
    return currentUnit;
  }

  return defaultProductUnit;
};

export default class extends Controller<HTMLDivElement> {
  static targets = [
    "product",
    "productTextField",
    "price",
    "discount",
    "quantity",
    "subtotal",
    "taxRate"
  ];

  static values = {
    currency: String,
    entry: Boolean,
    enableDisableFields: { type: Boolean, default: true }
  };

  declare currencyValue: string;
  declare readonly hasCurrencyValue: boolean;

  declare entryValue: boolean;
  declare readonly hasEntryValue: boolean;

  declare enableDisableFieldsValue: boolean;
  declare readonly hasEnableDisableFieldsValue: boolean;

  declare readonly hasProductTarget: boolean;
  declare readonly productTarget: HTMLInputElement;

  declare readonly hasProductTextFieldTarget: boolean;
  declare readonly productTextFieldTarget: HTMLInputElement;

  declare readonly hasPriceTarget: boolean;
  declare readonly priceTarget: ImaskedElement<HTMLInputElement>;

  declare readonly hasDiscountTarget: boolean;
  declare readonly discountTarget: HTMLInputElement;

  declare readonly hasQuantityTarget: boolean;
  declare readonly quantityTarget: HTMLInputElement;

  declare readonly hasTaxRateTarget: boolean;
  declare readonly taxRateTarget: HTMLElement;

  declare readonly hasSubtotalTarget: boolean;
  declare readonly subtotalTarget:
    | HTMLDivElement
    | ImaskedElement<HTMLInputElement>;

  productPrice;
  taxRate = 0.0;

  connect() {
    this.parseTaxRateValue();
    this.enableDisableFields();
  }

  parseTaxRateValue() {
    if (this.hasTaxRateTarget && this.taxRateTarget.innerHTML.trim() !== "") {
      this.taxRate = parseFloat(this.taxRateTarget.innerHTML);
    }
  }

  productChanged(e) {
    const [quantityString, unitOfMeasureString] =
      this.quantityTarget.value.split(/\s+/);
    const quantity = parseFloat(quantityString || "1");
    const {
      unitOfMeasure: defaultUnitOfMeasure,
      equivalencies,
      taxRate
    } = e.detail.selected.dataset;
    const unitOfMeasure = getValidUnitOfMeasure(
      unitOfMeasureString,
      defaultUnitOfMeasure,
      JSON.parse(equivalencies)
    );

    this.quantityTarget.value = `${quantity} ${unitOfMeasure}`;

    this.productPrice = this.entryValue
      ? e.detail.selected.dataset.cost
      : e.detail.selected.dataset.price;
    if (this.priceTarget.imask) {
      this.priceTarget.imask.unmaskedValue = this.productPrice ?? "";
    } else {
      this.priceTarget.value = this.productPrice;
    }

    if (taxRate) {
      this.taxRate = parseFloat(taxRate);
      if (this.hasTaxRateTarget) {
        this.taxRateTarget.innerHTML = `${this.taxRate}%`;
      }
    } else {
      this.taxRate = 0;
      if (this.hasTaxRateTarget) {
        this.taxRateTarget.innerHTML = "N/A";
      }
    }

    this.dispatch("productChanged", {
      detail: { productId: this.productTarget.value }
    });

    this.enableDisableFields();
    this.updateLineSubtotal();
  }

  focusProductInput() {
    this.productTextFieldTarget.focus();
  }

  updateLineSubtotal() {
    this.parseTaxRateValue();

    const unitPrice = this.unitPrice;
    const quantity = this.quantity;
    let discount = 0.0;
    if (this.hasDiscountTarget) {
      discount = this.discount;
    }
    let taxRate = 0.0;
    if (this.hasTaxRateTarget) {
      taxRate = this.taxRate / 100;
    }
    const subtotal = unitPrice * quantity;

    const taxes = subtotal * taxRate;

    this.element.dataset.subtotal = `${subtotal + taxes}`;
    this.element.dataset.taxes = `${taxes}`;
    this.element.dataset.discount = `${discount}`;

    this.updateSubtotalLabelOrInput();

    const event = new CustomEvent("changed", {
      detail: { lineItem: this.element }
    });
    this.element.dispatchEvent(event);
  }

  updateLinePrice() {
    if (this.subtotalTarget instanceof HTMLInputElement) {
      const subtotal =
        parseFloat(
          this.subtotalTarget.imask
            ? this.subtotalTarget.imask.unmaskedValue
            : this.subtotalTarget.value
        ) || 0.0;
      this.element.dataset.subtotal = `${subtotal}`;

      const unitPrice = (subtotal * 100) / (this.taxRate + 100) / this.quantity;

      this.priceTarget.imask.unmaskedValue = `${unitPrice}`;
    }
  }

  updateCurrency(e) {
    this.currencyValue = e.detail.currency;

    // The setTimeout is to allow the input's currency to be updated
    // before updating the subtotal label or input
    setTimeout(() => {
      this.updateSubtotalLabelOrInput();
    });

    if (this.hasDiscountTarget) {
      const controller = this.discountTarget.closest<HTMLDivElement>(
        '[data-controller*="inputs--percentage-or-currency"]'
      );
      if (controller) {
        controller.dataset["inputs-PercentageOrCurrencyCurrencyValue"] =
          currencySymbols[this.currencyValue];
      }
    }
  }

  updateSubtotalLabelOrInput() {
    if (this.subtotalTarget instanceof HTMLInputElement) {
      if (this.subtotalTarget.imask) {
        this.subtotalTarget.imask.unmaskedValue = `${this.subtotal}`;
      }
    } else {
      this.subtotalTarget.innerHTML = IMask.pipe(
        `${this.subtotal}`,
        {
          ...this.priceTarget.imaskOptions,
          blocks: {
            num: {
              // @ts-expect-error - our IMask typings are not correct
              ...this.priceTarget.imaskOptions.blocks.num,
              mapToRadix: ["."]
            }
          }
        },
        IMask.PIPE_TYPE.UNMASKED
      );
    }
  }

  enableDisableFields() {
    if (
      (this.hasProductTextFieldTarget &&
        this.productTextFieldTarget.disabled) ||
      !this.enableDisableFieldsValue
    ) {
      return;
    }

    const disabled = Boolean(
      !this.productTarget.value || this.productTarget.value === ""
    );
    this.quantityTarget.disabled = disabled;
    this.priceTarget.disabled = disabled;

    if (this.hasDiscountTarget) {
      this.discountTarget.disabled = disabled;
    }
  }

  get subtotal(): number {
    return parseFloat(this.element.dataset.subtotal ?? "0.0");
  }

  get unitPrice(): number {
    return (
      parseFloat(
        this.priceTarget.imask
          ? this.priceTarget.imask.unmaskedValue
          : this.priceTarget.value
      ) || 0.0
    );
  }

  get quantity(): number {
    return parseFloat(this.quantityTarget.value) || 0.0;
  }

  get discount(): number {
    if (
      this.discountTarget.value &&
      this.discountTarget.value.trim().indexOf("%") ===
        this.discountTarget.value.trim().length - 1
    ) {
      const percentage = parseFloat(this.discountTarget.value) || 0.0;
      return this.unitPrice * this.quantity * (percentage / 100);
    }
    return (
      parseFloat(this.discountTarget.value.replace(/[^0-9,\.]/, "")) || 0.0
    );
  }
}
